import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import ReactPlayer from 'react-player'
import { createContainer } from 'unstated-next'

import useSwr from '../../../../../hooks/use-swr'
import GoogleAnalyticsTrackingStore from '../../../../../modules/tracking/stores/google-analytics-tracking-store'
import MixpanelTrackingStore from '../../../../../modules/tracking/stores/mixpanel-tracking-store'
import PixelTrackingStore from '../../../../../modules/tracking/stores/pixel-tracking-store'
import AuthStore from '../../../../../modules/user/stores/auth-store'
import broadcastUtils from '../../../../../utils/broadcast'
import { ActiveSubscriptionParser } from '../../../../pricing/home/utils/parser'
import BroadcastClassApis from '../../../api'
import BroadcastHomeStore from './broadcast-home-store'

const BroadcastRegistrationStore = createContainer(() => {
  const router = useRouter()
  const {
    query: { livdemyId, bcType, url, version },
    isFallback,
  } = router
  const { loggedIn } = AuthStore.useContainer()
  const { logEvent } = GoogleAnalyticsTrackingStore.useContainer()
  const { track } = PixelTrackingStore.useContainer()
  const { trackEvent } = MixpanelTrackingStore.useContainer()
  const [registering, toggleRegistering] = useState(false)

  const { data: bcData } = BroadcastHomeStore.useContainer()

  const { data, error, revalidate } = useSwr({
    condition:
      !isFallback &&
      Boolean(bcData?.id) &&
      Boolean(broadcastUtils.getByRoute(bcType)?.type),
    errorNotification: false,
    server: 'sls-core',
    url: `broadcast-class/id/${bcData?.id}/type/${
      broadcastUtils.getByRoute(bcType)?.type
    }/registrations`,
  })

  const { data: activeSubscription } = useSwr({
    condition: loggedIn,
    parser: ActiveSubscriptionParser,
    url: `subscriptions`,
  })

  const register = useCallback(async () => {
    logEvent({
      action: 'registration attempted',
      category: 'conversion',
      label: `${bcData?.name} by ${bcData?.artist}`,
    })

    const { success } = await BroadcastClassApis.register(
      livdemyId,
      broadcastUtils.getByRoute(bcType)?.type,
      bcData?.id,
    )

    if (success) {
      logEvent({
        action: 'registration complete',
        category: 'conversion',
        label: `/${livdemyId}/${bcType}/${url}/${version}`,
      })
      trackEvent(
        `${broadcastUtils.getByRoute(bcType).name}-registration-complete`,
        {
          artist: bcData?.artist,
          id: bcData?.id,
          name: bcData?.name,
          route: `/${livdemyId}/${bcType}/${url}/${version}`,
        },
      )
      track('AddToCart')

      await revalidate()
      await router.push(
        `/${livdemyId}/${bcType}/${url}/${version}/payment/live`,
      )
    }

    return success
  }, [
    logEvent,
    bcData,
    livdemyId,
    bcType,
    url,
    version,
    trackEvent,
    track,
    revalidate,
    router,
  ])

  const purchaseRecording = useCallback(async () => {
    logEvent({
      action: 'recording purchase attempted',
      category: 'conversion',
      label: `${bcData?.name} by ${bcData?.artist}`,
    })

    const { success } = await BroadcastClassApis.purchaseRecording(
      broadcastUtils.getByRoute(bcType)?.type,
      bcData?.id,
    )

    if (success) {
      logEvent({
        action: 'recording purchase registration complete',
        category: 'conversion',
        label: `/${livdemyId}/${bcType}/${url}/${version}`,
      })
      trackEvent(
        `${
          broadcastUtils.getByRoute(bcType).name
        }-recording-purchase-registration`,
        {
          artist: bcData?.artist,
          id: bcData?.id,
          name: bcData?.name,
          route: `/${livdemyId}/${bcType}/${url}/${version}`,
        },
      )
      track('AddToCart')

      await revalidate()
    }

    return success
  }, [
    bcData,
    bcType,
    livdemyId,
    logEvent,
    revalidate,
    track,
    trackEvent,
    url,
    version,
  ])

  const getRecordingFile = useCallback(async () => {
    const id = data?.recording?.id

    if (!id) {
      return false
    }

    if (ReactPlayer.canPlay(data?.recording?.id)) {
      return {
        name: data?.name,
        type: 'video/mp4',
        url: data?.recording?.id,
      }
    }

    const { success, result } = await BroadcastClassApis.getRecordingFile(id)

    return (
      success && {
        data: result,
        name: data?.name,
        type: 'protected-video',
        url: '',
      }
    )
  }, [data])

  return {
    activeSubscription,
    data,
    getRecordingFile,
    loadFailed: Boolean(error) && !data,
    loading: !data && !error,
    purchaseRecording,
    register,
    registering,
    revalidate,
    toggleRegistering,
  }
})

export default BroadcastRegistrationStore
